// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import api, { crmApiUrl } from './api'
import { saveBlob } from '../../lib/download'

export interface CustomerAttachment {
  id: string
  name: string
  size: number
  type: string
  original_filename: string
  content_type: string
  file_size: number
  created_at: string
  uploaded_by: {
    id: string
    name: string
  }
}

export interface CustomerAttachmentUploadResponse {
  success: boolean
  attachment: CustomerAttachment
  message?: string
  error?: string
}

export interface CustomerAttachmentsListResponse {
  success: boolean
  attachments: CustomerAttachment[]
  pagination?: {
    current_page: number
    per_page: number
    total_count: number
  }
  error?: string
}

class CustomerAttachmentsApi {
  // Get all attachments for a customer
  async getAttachments(
    customerId: string,
    params?: {
      page?: number
      per_page?: number
      search?: string
    }
  ): Promise<CustomerAttachmentsListResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', params.page.toString())
    if (params?.per_page) query.append('per_page', params.per_page.toString())
    if (params?.search) query.append('search', params.search)
    
    const url = `/customers/${customerId}/attachments${query.toString() ? '?' + query.toString() : ''}`
    const response = await api.get(url)
    return response.data
  }

  // Upload file attachment to customer
  async uploadFile(
    customerId: string,
    file: File
  ): Promise<CustomerAttachmentUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(`/customers/${customerId}/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data
  }

  // Download attachment
  async downloadFile(customerId: string, attachmentId: string, filename?: string): Promise<void> {
    const url = `/customers/${customerId}/attachments/${attachmentId}/download`
    
    try {
      const response = await api.get(url, {
        responseType: 'blob',
      })
      
      // Native OS Save-As dialog in the desktop app; browser download on the web.
      await saveBlob(filename || `attachment-${attachmentId}`, new Blob([response.data]))
    } catch (error) {
      console.error('Download failed:', error)
      throw error
    }
  }

  // Delete file attachment from customer
  async deleteFile(customerId: string, attachmentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.delete(`/customers/${customerId}/attachments/${attachmentId}`)
      return response.data
    } catch (error) {
      console.error('Delete failed:', error)
      throw error
    }
  }

  // Get file content for viewing (text files, CSV, etc.)
  async getFileContent(customerId: string, attachmentId: string): Promise<{ data: string }> {
    try {
      const response = await api.get(`/customers/${customerId}/attachments/${attachmentId}/view`, {
        responseType: 'text'
      })
      return { data: response.data }
    } catch (error) {
      console.error('Failed to get file content:', error)
      throw error
    }
  }

  // RESELLER PORT (spec 2026-09-09 §3.3): the URL `CustomerAttachmentViewer`
  // builds for its raw-`fetch` branches (image/video/audio blob loading,
  // and PDF/Excel/Word in `loadDocumentContent`) — scoped through here
  // rather than hardcoded in the viewer, and through `crmApiUrl` so it
  // reaches `/api/crm/v1/...`, not a doubled `/api/v1` prefix. See
  // src/crm/README.md.
  viewUrl(customerId: string, attachmentId: string): string {
    return crmApiUrl(`/customers/${customerId}/attachments/${attachmentId}/view`)
  }

  // Validate file before upload
  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 50 * 1024 * 1024 // 50MB in bytes
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
      'image/svg+xml',
      'application/zip',
      'application/x-zip-compressed',
      'application/x-rar-compressed',
      'application/x-7z-compressed'
    ]

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size too large. Maximum size for customer attachments is 50MB.'
      }
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file type. Only PDF, Office documents, images, text files and archives are allowed.'
      }
    }

    return { valid: true }
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }
}

export const customerAttachmentsApi = new CustomerAttachmentsApi()
export default customerAttachmentsApi

